import { useCallback, useRef, useState } from 'react'

import type { AiEncryptionKey } from '@/src/domains/ai/types'

interface PendingEncryptionKey {
  promise: Promise<AiEncryptionKey | null>
  resolve: (value: AiEncryptionKey | null) => void
}

export const useAiSetupProviderEncryptionKey = () => {
  const [encryptionKey, setEncryptionKey] = useState<AiEncryptionKey | null>(null)

  // Read via a ref so the callbacks stay stable and older closures (useChat's onFinish) still see the key.
  const encryptionKeyRef = useRef<AiEncryptionKey | null>(null)
  // Every caller waits on the one request.
  const pendingRef = useRef<PendingEncryptionKey | null>(null)

  const hasEncryptionKey = useCallback(() => encryptionKeyRef.current !== null, [])

  const requestEncryptionKey = useCallback(() => {
    if (encryptionKeyRef.current) {
      return Promise.resolve(encryptionKeyRef.current)
    }

    if (!pendingRef.current) {
      let resolve!: PendingEncryptionKey['resolve']
      const promise = new Promise<AiEncryptionKey | null>((resolvePromise) => {
        resolve = resolvePromise
      })

      pendingRef.current = { promise, resolve }
    }

    return pendingRef.current.promise
  }, [])

  const settlePendingEncryptionKey = useCallback(() => {
    pendingRef.current?.resolve(encryptionKeyRef.current)
    pendingRef.current = null
  }, [])

  const submitEncryptionKey = useCallback(
    (key: AiEncryptionKey) => {
      encryptionKeyRef.current = key
      setEncryptionKey(key)
      settlePendingEncryptionKey()
    },
    [settlePendingEncryptionKey],
  )

  return {
    encryptionKey,
    hasEncryptionKey,
    requestEncryptionKey,
    submitEncryptionKey,
    settlePendingEncryptionKey,
  }
}
